/**
 * @file useSwipe.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-03
 * @modified 2026-09-03
 *
 * Horizontal swipe on a touch surface, using Pointer Events.
 *
 * Hand-rolled rather than pulling in react-swipeable / embla / framer-motion:
 * this is the only gesture in the app, chart.js is the sole front-end library
 * we carry, and none of those is worth the bundle or the dependency review for
 * one widget.
 *
 * The rules below are the ones that are easy to get wrong:
 *
 *  - AXIS LOCK. Abandon the gesture the moment vertical movement dominates,
 *    otherwise the swipe eats the page scroll — the classic failure.
 *  - `touch-action: pan-y` MUST be set on the surface in CSS. That, not
 *    preventDefault, is what stops the browser fighting the drag; pointermove
 *    listeners are passive by default so preventDefault there is ignored.
 *  - setPointerCapture so a drag that leaves the element still completes, and
 *    handle pointercancel (a call, a notification, a system gesture).
 *  - iOS reserves a left-edge horizontal drag for its back gesture and wins.
 *    Inset the surface from the left edge; a swipe starting there won't fire.
 *
 * A swipe is undiscoverable and unavailable to keyboard and screen-reader
 * users, so it must always be a shortcut for a control that also exists.
 */

import { useEffect, useRef, useState } from 'react';

export default function useSwipe(ref, {
  onLeft,
  onRight,
  threshold = 48,
  maxOffAxis = 40,
  enabled = true
} = {}) {
  // Live drag distance, so the caller can translate the surface for feedback.
  const [dx, setDx] = useState(0);
  const state = useRef({ active: false, startX: 0, startY: 0, locked: null, id: null });

  useEffect(() => {
    const el = ref.current;
    if (!el || !enabled) return;

    const reset = () => {
      state.current.active = false;
      state.current.locked = null;
      state.current.id = null;
      setDx(0);
    };

    const onDown = (e) => {
      if (!e.isPrimary) return;
      state.current = { active: true, startX: e.clientX, startY: e.clientY, locked: null, id: e.pointerId };
    };

    const onMove = (e) => {
      const s = state.current;
      if (!s.active || e.pointerId !== s.id) return;

      const dX = e.clientX - s.startX;
      const dY = e.clientY - s.startY;

      // Decide once, within the first few pixels, which axis this gesture is.
      if (s.locked === null) {
        if (Math.abs(dX) < 10 && Math.abs(dY) < 10) return;
        s.locked = Math.abs(dX) > Math.abs(dY) ? 'x' : 'y';
        if (s.locked === 'x') {
          try { el.setPointerCapture(e.pointerId); } catch { /* capture is best-effort */ }
        }
      }
      if (s.locked !== 'x') return;
      if (Math.abs(dY) > maxOffAxis * 3) { reset(); return; }

      setDx(dX);
    };

    const onUp = (e) => {
      const s = state.current;
      if (!s.active || e.pointerId !== s.id) return;

      const dX = e.clientX - s.startX;
      const dY = e.clientY - s.startY;
      const committed = s.locked === 'x' && Math.abs(dX) >= threshold && Math.abs(dY) <= maxOffAxis;

      try { el.releasePointerCapture(e.pointerId); } catch { /* may already be released */ }
      reset();

      if (!committed) return;
      if (dX < 0) onLeft?.();
      else onRight?.();
    };

    el.addEventListener('pointerdown', onDown);
    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerup', onUp);
    el.addEventListener('pointercancel', reset);
    return () => {
      el.removeEventListener('pointerdown', onDown);
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerup', onUp);
      el.removeEventListener('pointercancel', reset);
    };
  }, [ref, onLeft, onRight, threshold, maxOffAxis, enabled]);

  return { dx };
}
